import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { DbService } from './db.service';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TicketService {

  constructor(
    private authService: AuthService,
    private dbService: DbService,
    private http: HttpClient
  ) {}

  getTicketData(ticketId: string) {
    return this.dbService.listen<any>(`accounts/${this.authService.currentUser.account_id}/ticket_system/tickets/${ticketId}`);
  }

  getRoomTicketList(roomId: string): Observable<any[]> {
    return new Observable<any[]>(subscriber => {
      this.getTickets()
        .then(res => {
          const tickets = (res?.tickets || []).filter(ticket => {
            const ticketRoomId = ticket.room?.id || ticket.room_id;
            return ticketRoomId === roomId;
          }).sort((a, b) => (b.create_time || 0) - (a.create_time || 0));
          subscriber.next(tickets);
          subscriber.complete();
        })
        .catch(() => {
          subscriber.next([]);
          subscriber.complete();
        });
    });
  }

  getTicketSessions(ticketId: string): Observable<any[]> {
    return new Observable<any[]>(subscriber => {
      const ticketSessions: any[] = [];
      subscriber.next(ticketSessions);
      const sub1 = this.dbService.querySnap<any>(`accounts/${this.authService.currentUser.account_id}/sessions_meta`, [{key:"orderByChild",value:"attached_ticket"}, {key:"equalTo",value:ticketId}], "child_added")
      .subscribe(snp => {
        const session = snp.data;
        session.id = snp.key;
        ticketSessions.push(session);
        subscriber.next(ticketSessions);
      });
      const sub2 = this.dbService.querySnap<any>(`accounts/${this.authService.currentUser.account_id}/sessions_meta`, [{key:"orderByChild",value:"attached_ticket"}, {key:"equalTo",value:ticketId}], "child_changed")
      .subscribe(snp => {
        const i = ticketSessions.findIndex(s => s.id === snp.key);
        const session = snp.data;
        session.id = snp.key;
        if (i>-1) {
          ticketSessions[i] = session;
        } else {
          ticketSessions.push(session);
        }
        subscriber.next(ticketSessions);
      });
      const sub3 = this.dbService.querySnap<any>(`accounts/${this.authService.currentUser.account_id}/sessions_meta`, [{key:"orderByChild",value:"attached_ticket"}, {key:"equalTo",value:ticketId}], "child_removed")
      .subscribe(snp => {
        const i = ticketSessions.findIndex(s => s.id === snp.key);
        if (i>-1) {
          ticketSessions.splice(i, 1);
        }
        subscriber.next(ticketSessions);
      });

      return () => {
        sub1.unsubscribe();
        sub2.unsubscribe();
        sub3.unsubscribe();
      }
    });
  }

  getTickets() {
    const url = environment.endPoints.getUserTickets;
    return this.http.post<any>(url, { token: this.authService.currentUser.token }).toPromise();
  }

  getTicketDetail(ticketId: string) {
    const url = environment.endPoints.getTicketDetails;
    return this.http.post<any>(url, { token: this.authService.currentUser.token, ticket_id: ticketId }).toPromise();
  }

  createTicket(ticketData: any) {
    return this.http.post<any>(environment.endPoints.createticket, {token: this.authService.currentUser.token, ticket: ticketData}).pipe(first()).toPromise();
  }

  attachTicket(ticketId: string, roomId: string, sessionId: string) {
    return this.http.post<any>(environment.endPoints.attachticket, {token: this.authService.currentUser.token, ticket_id: ticketId, room_id: roomId, session_id: sessionId}).pipe(first()).toPromise();
  }

  detachTicket(ticketId: string, roomId: string, sessionId: string) {
    return this.http.post<any>(environment.endPoints.detachticket, {token: this.authService.currentUser.token, ticket_id: ticketId, room_id: roomId, session_id: sessionId}).pipe(first()).toPromise();
  }

  updateTicket(changes: any) {
    return this.http.post<any>(environment.endPoints.updateticket, {token: this.authService.currentUser.token, changes: changes}).pipe(first()).toPromise();
  }

  deleteTicket(ticketId: string) {
    return this.http.post<any>(environment.endPoints.deleteticket, {token: this.authService.currentUser.token, ticket: ticketId}).pipe(first()).toPromise();
  }

  deleteTicketFile(ticketId: string, fileId: string) {
    return this.http.post<any>(environment.endPoints.deleteTicketFile, {
      token: this.authService.currentUser.token,
      ticketId,
      fileId
    }).pipe(first()).toPromise();
  }

  getTicketFiles(ticketId) {
    return this.dbService.listen<any>(`accounts/${this.authService.currentUser.account_id}/ticket_system/tickets_files/${ticketId}`);
  }

  uploadTicketFiles(formData: any, ticketId: string, token: string) {
    const url = `https://cdn.vsight.link/upload-ticket-files/${ticketId}?env=${environment.name}`;
    const uploadTimestamp = Date.now();
    
    return this.http.post<any>(url, formData, { headers: { authorization: `Bearer ${token}`, token: token } })
      .pipe(first())
      .toPromise()
      .then(response => {
        const files = response?.data?.files || response?.files;
        if (files && Array.isArray(files)) {
          const promises = files.map(file => {
            if (file.succeeded && file.id) {
              const filePath = `accounts/${this.authService.currentUser.account_id}/ticket_system/tickets_files/${ticketId}/${file.id}`;
              return this.dbService.update(filePath, {
                uploader_id: this.authService.currentUser.id,
                uploader_name: this.authService.currentUser.name,
                create_time: uploadTimestamp
              }).catch(err => {
                console.error('Error adding uploader info:', err);
              });
            }
            return Promise.resolve();
          });
          return Promise.all(promises).then(() => response);
        }
        return response;
      });
  }

  getTicketTypes() {
    const url = environment.endPoints.getTicketTypes;
    return this.http.post<any>(url, { token: this.authService.currentUser.token }).toPromise();
  }

  saveTicketTypes(ticketTypes: any[]) {
    const url = environment.endPoints.saveTicketTypes;
    return this.http.post<any>(url, { token: this.authService.currentUser.token, ticket_types: ticketTypes }).pipe(first()).toPromise();
  }

  addTicketChatMessage(ticketId: string, message: { text: string; userId: string; timestamp: number }) {
    const url = environment.endPoints.addTicketChat;
    return this.http.post<any>(url, { 
      token: this.authService.currentUser.token, 
      ticket_id: ticketId, 
      message: message 
    }).pipe(first()).toPromise();
  }
}
